import React from 'react';
import { X, GitCommit, CheckCircle2, ShieldCheck, Sparkles, Tag, Calendar, Layers } from 'lucide-react';
import { VERSION_HISTORY, CURRENT_VERSION, ADMIN_EMAIL } from '../lib/versionHistory';

interface VersionHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  userEmail?: string | null;
}

export default function VersionHistoryModal({ isOpen, onClose, userEmail }: VersionHistoryModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-gray-950/60 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6 animate-fadeIn">
      <div className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-3xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden dir-rtl">
        
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-100 dark:border-gray-800/80 flex items-center justify-between bg-gradient-to-r from-indigo-50/50 via-purple-50/30 to-emerald-50/30 dark:from-indigo-950/30 dark:via-purple-950/20 dark:to-emerald-950/20">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-indigo-600 text-white flex items-center justify-center shadow-md shadow-indigo-500/20">
              <Layers className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base font-extrabold text-gray-900 dark:text-white font-display">
                  تاریخچه تغییرات و نسخه‌ها
                </h2>
                <span className="bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300 text-[10px] font-bold px-2 py-0.5 rounded-full font-mono">
                  v{CURRENT_VERSION}
                </span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 font-medium mt-0.5">
                لیست تمام به‌روزرسانی‌ها و قابلیت‌های افزوده شده
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Security Badge Banner */}
        <div className="bg-emerald-50/80 dark:bg-emerald-950/30 px-6 py-2.5 border-b border-emerald-100 dark:border-emerald-900/40 flex items-center justify-between text-xs text-emerald-800 dark:text-emerald-300">
          <div className="flex items-center gap-2 font-medium">
            <ShieldCheck className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
            <span>مشاهده مجاز برای مدیریت: <strong className="font-mono text-[11px]">{ADMIN_EMAIL}</strong></span>
          </div>
          <span className="text-[10px] font-bold bg-emerald-200/60 dark:bg-emerald-900/60 text-emerald-900 dark:text-emerald-200 px-2 py-0.5 rounded-md">
            سامانه پیشرفت نسخه {CURRENT_VERSION}
          </span>
        </div>

        {/* Version Timeline Content */}
        <div className="p-6 overflow-y-auto space-y-6 flex-1">
          {VERSION_HISTORY.map((item, index) => {
            const isCurrent = item.version === CURRENT_VERSION;

            return (
              <div 
                key={item.version}
                className={`relative pr-6 border-r-2 ${
                  isCurrent 
                    ? 'border-indigo-500 dark:border-indigo-400' 
                    : 'border-gray-200 dark:border-gray-800'
                }`}
              >
                {/* Timeline Dot */}
                <div 
                  className={`absolute -right-[9px] top-0.5 w-4 h-4 rounded-full border-2 bg-white dark:bg-gray-900 flex items-center justify-center ${
                    isCurrent 
                      ? 'border-indigo-600 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400 ring-4 ring-indigo-100 dark:ring-indigo-950/50' 
                      : 'border-gray-300 dark:border-gray-700'
                  }`}
                >
                  <div className={`w-1.5 h-1.5 rounded-full ${isCurrent ? 'bg-indigo-600 dark:bg-indigo-400' : 'bg-gray-400'}`} />
                </div>

                {/* Card Header */}
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-extrabold font-mono px-2.5 py-0.5 rounded-lg ${
                      isCurrent 
                        ? 'bg-indigo-600 text-white shadow-xs' 
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                    }`}>
                      نسخه {item.version}
                    </span>

                    {item.badge && (
                      <span className="bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 text-[10px] font-bold px-2 py-0.5 rounded-md flex items-center gap-1">
                        <Sparkles className="w-3 h-3" />
                        {item.badge}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-1 text-[11px] text-gray-400 dark:text-gray-500 font-mono">
                    <Calendar className="w-3.5 h-3.5" />
                    <span>{item.date}</span>
                  </div>
                </div>

                {/* Title */}
                <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-2.5">
                  {item.title}
                </h3>

                {/* Bullet Points */}
                <ul className="space-y-1.5 bg-gray-50/70 dark:bg-gray-800/40 p-3.5 rounded-2xl border border-gray-100 dark:border-gray-800/60">
                  {item.changes.map((change, cIdx) => (
                    <li key={cIdx} className="text-xs text-gray-600 dark:text-gray-300 flex items-start gap-2 leading-relaxed">
                      <CheckCircle2 className="w-3.5 h-3.5 text-indigo-500 dark:text-indigo-400 shrink-0 mt-0.5" />
                      <span>{change}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/60 flex items-center justify-between">
          <span className="text-xs text-gray-400 font-medium">
            برنامه سلامتی و یادگیری پیشرفت &bull; نسخه {CURRENT_VERSION}
          </span>
          <button
            onClick={onClose}
            className="bg-gray-900 dark:bg-gray-100 hover:bg-gray-800 dark:hover:bg-white text-white dark:text-gray-900 px-5 py-2 rounded-xl text-xs font-bold transition-colors cursor-pointer"
          >
            بستن
          </button>
        </div>

      </div>
    </div>
  );
}
